/**
 * 21/10/2026 — the engine settings behind the Admin guide (user: "editable
 * means instead of fetching stats every 15 min I could edit, saying every
 * 20 min — I want to see everything and have full control"). Read only by
 * whoever holds the permission (admins hold every one); a save puts the
 * saved set straight into the cache, so every section shows it at once.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DropGenEngineSettings } from '@trafficpulse/shared';

import { dropGeneratorKeys } from '@/features/drop-generator/useDropGenerator';
import { useAuth } from '@/providers/AuthProvider';
import { api } from '@/services/api';

export const ENGINE_SETTINGS_PERMISSION = 'drop_generator.engine_settings';
const URL = '/drop-generator/engine-settings';
export const engineSettingsKey = [...dropGeneratorKeys.all, 'engine-settings'] as const;

interface Envelope<T> {
  data: T;
}

/** The Admin guide and its settings: admin-only unless a role is granted the permission. */
export function useCanSeeAdminGuide(): boolean {
  return useAuth().hasPermission(ENGINE_SETTINGS_PERMISSION);
}

export function useEngineSettings() {
  const enabled = useCanSeeAdminGuide();
  return useQuery({
    queryKey: engineSettingsKey,
    queryFn: async () => (await api.get<Envelope<DropGenEngineSettings>>(URL)).data.data,
    enabled,
    staleTime: 30_000,
  });
}

export function useSaveEngineSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (settings: Partial<DropGenEngineSettings>) =>
      (await api.put<Envelope<DropGenEngineSettings>>(URL, { settings })).data.data,
    onSuccess: (saved) => queryClient.setQueryData(engineSettingsKey, saved),
  });
}
